import type React from 'react';

import { cn } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';

import { useTypewriterPlaceholder } from './hooks/useTypewriterPlaceholder';

export type TypewriterPlaceholderProps = {
	/**
	 * Phrases to cycle through. Pass a module-level array of literal `translationKey('…')` calls:
	 * the string extractor and the no-invalid-translation-keys rule find keys statically, so keys
	 * built at runtime are invisible to both.
	 */
	phrases: readonly TranslationKey[];
	className?: string;
};

/**
 * Decorative placeholder that types example phrases over an input. A native `placeholder` can't be
 * animated, so this is an overlay the consumer positions over the field; see
 * `useTypewriterPlaceholder` for the cycle itself.
 *
 * A phrase wider than the field wraps onto a second line and is clamped there, so `className` must
 * give it a bounded width (both `left` and `right` when absolute, not `left` alone) and leave two
 * lines of clearance below `top`. Visibility is also the consumer's, e.g.
 * `group-has-[textarea:focus]:opacity-0`.
 */
export const TypewriterPlaceholder: React.FC<TypewriterPlaceholderProps> = ({ phrases, className }) => {
	const {
		data: { hasPhrase, text, phase },
	} = useTypewriterPlaceholder(phrases);

	if (!hasPhrase) return null;

	return (
		// Decorative: the field keeps its own accessible name, and announcing a half-typed string
		// would be noise. `current/50` is the colour a native placeholder gets, so the typed text
		// reads the same as the static one it stands in for.
		<div
			aria-hidden="true"
			data-qa="typewriter-placeholder"
			data-phase={phase}
			className={cn('text-size-body pointer-events-none line-clamp-2 text-current/50', className)}
		>
			<span data-qa="typewriter-placeholder-phrase">{text}</span>
			{/* inline rather than a flex sibling so it follows the text onto the second line */}
			<span
				data-qa="typewriter-placeholder-caret"
				className="animate-typewriter-caret ml-px inline-block h-[1em] w-px translate-y-[0.15em] bg-current/50 motion-reduce:animate-none"
			/>
		</div>
	);
};
